import React, { useState, useEffect } from 'react';
import './CreateSession.css';

const CreateSession = ({ organizerId }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [persona, setPersona] = useState(null);
  const [isPersonaFetched, setIsPersonaFetched] = useState(false);

  const [formData, setFormData] = useState({
    session_title: '',
    difficulty: 'hard',
    participants: [{ name: '', email: '' }],
  });

  const personaSessionTitleMap = {
    investor: "Startup Evaluation Session",
    founder: "Investor Readiness Interview",
    recruiter: "Candidate Screening Session",
    hr_manager: "Employee Assessment Session",
    vendor: "Vendor Evaluation Session",
  };

  useEffect(() => {
    if (isPersonaFetched) return;
    setIsPersonaFetched(true);
    fetch(`/api/organizer/${organizerId}`, {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        const p = data.persona;
        console.log('Fetched organizer persona:', data);
        setPersona(p);
        setFormData(prev => ({
          ...prev,
          session_title: personaSessionTitleMap[p] || 'Evaluation Session',
        }));
      });
  }, [organizerId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleParticipantChange = (index, field, value) => {
    const newParticipants = [...formData.participants];
    newParticipants[index][field] = value;
    setFormData({ ...formData, participants: newParticipants });
  };

  const addParticipant = () => {
    setFormData({
      ...formData,
      participants: [...formData.participants, { name: '', email: '' }],
    });
  };

  const removeParticipant = (index) => {
    const newParticipants = formData.participants.filter((_, i) => i !== index);
    setFormData({ ...formData, participants: newParticipants });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Create session - persona, job_title, and topic come from organizer's onboarding
      const sessionResponse = await fetch(`/api/organizer/${organizerId}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          session_title: formData.session_title,
          difficulty: formData.difficulty,
        }),
      });

      const sessionData = await sessionResponse.json();

      if (!sessionResponse.ok) {
        setMessage(sessionData.error || 'Failed to create session');
        setLoading(false);
        return;
      }

      // Create invites
      const validParticipants = formData.participants.filter(
        (p) => p.email && p.email.trim()
      );

      if (validParticipants.length === 0) {
        setMessage('Please add at least one participant with an email');
        setLoading(false);
        return;
      }

      const invitesResponse = await fetch(
        `/api/organizer/sessions/${sessionData.session_id}/invites`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            session_title: formData.session_title,
            participants: validParticipants,
          }),
        }
      );

      const invitesData = await invitesResponse.json();

      if (invitesResponse.ok) {
        setMessage(
          `Session created successfully! ${invitesData.invites.length} invite(s) generated.`
        );
        // Reset form
        setFormData({
          difficulty: 'hard',
          participants: [{ name: '', email: '' }],
        });
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(invitesData.error || 'Session created but invites failed');
      }
    } catch (err) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-session">
      <h2>Send Test Link</h2>
      <p className="subtitle">Create a new screening session and send invites to candidates</p>

      {message && (
        <div className={message.includes('success') ? 'success-message' : 'error-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Difficulty level is set to 'hard' by default and hidden from UI */}

        <div className="form-group">
          <label>Session Title</label>
          <input
            type="text"
            name="session_title"
            placeholder="e.g. Startup Evaluation Session"
            value={formData.session_title}
            onChange={handleChange}
            required
          />
          {persona && (
            <small className="hint">
              Suggested based on your persona ({persona})
            </small>
          )}
        </div>

        
        <div className="participants-section">
          <div className="section-header">
            <label>Participants</label>
            <button type="button" onClick={addParticipant} className="add-btn">
              + Add Participant
            </button>
          </div>

          {formData.participants.map((participant, index) => (
            <div key={index} className="participant-row">
              <input
                type="text"
                placeholder="Name"
                value={participant.name}
                onChange={(e) =>
                  handleParticipantChange(index, 'name', e.target.value)
                }
              />
              <input
                type="email"
                placeholder="Email *"
                value={participant.email}
                onChange={(e) =>
                  handleParticipantChange(index, 'email', e.target.value)
                }
                required
              />
              {formData.participants.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeParticipant(index)}
                  className="remove-btn"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? 'Creating...' : 'Create Session & Generate Invites'}
        </button>
      </form>
    </div>
  );
};

export default CreateSession;

